import { useState } from "react";
import type { Currency, FxRate, TaxSettings } from "@core/index";
import { useAppStore, selectCurrentRole } from "@state/store";
import { can } from "@data/index";
import { useBrand, useSetBrand } from "@branding/useBrand";
import { useLedger } from "@ui/useLedger";
import { MembersAccess } from "./MembersAccess";

export function Settings() {
  const brand = useBrand();
  const setBrand = useSetBrand();
  const { settings } = useLedger();
  const saveSettings = useAppStore((s) => s.saveSettings);
  const reset = useAppStore((s) => s.reset);
  const role = useAppStore(selectCurrentRole);
  const canEditSettings = can(role, "settings.edit");
  const canReset = can(role, "data.reset");

  const [draft, setDraft] = useState<TaxSettings>(settings);
  const [saved, setSaved] = useState(false);
  const [resetting, setResetting] = useState(false);

  function patch(p: Partial<TaxSettings>) {
    setDraft((d) => ({ ...d, ...p }));
    setSaved(false);
  }
  function patchRate(currency: Currency, rate: number) {
    setDraft((d) => ({
      ...d,
      fxRates: d.fxRates.map((r) => (r.currency === currency ? { ...r, rate } : r)),
    }));
    setSaved(false);
  }

  async function save() {
    await saveSettings(draft);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 3000);
  }

  async function doReset() {
    setResetting(true);
    await reset();
    setResetting(false);
  }

  const sbrEnabled = draft.sbReliefRevenueCeiling !== null;

  return (
    <section>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">White-label branding, currencies &amp; FX, and tax configuration.</div>
        </div>
        {import.meta.env.DEV && canReset ? (
          <div className="actions">
            <button type="button" className="btn danger" onClick={doReset} disabled={resetting}>
              {resetting ? "Resetting…" : "Reset demo data"}
            </button>
          </div>
        ) : null}
      </div>

      <div className="grid two">
        <div className="card">
          <div className="card-head">
            <h2>Branding</h2>
            <span className="top-spacer" />
            <span className="wire-note">◑ Live — edits re-skin the header instantly</span>
          </div>
          <div className="card-body">
            <div className="field">
              <label htmlFor="brName">Product name</label>
              <input
                id="brName"
                className="input"
                value={brand.name}
                onChange={(e) => setBrand({ name: e.target.value || "Ledgerline" })}
              />
              <span className="hint">Rendered in the header, browser tab title, and legal footer.</span>
            </div>
            <div className="field mt4">
              <label htmlFor="brTag">Tagline</label>
              <input
                id="brTag"
                className="input"
                value={brand.tagline}
                onChange={(e) => setBrand({ tagline: e.target.value })}
              />
            </div>
            <div className="field mt4">
              <label htmlFor="brFoot">Legal footer</label>
              <input
                id="brFoot"
                className="input"
                value={brand.legalFooter}
                onChange={(e) => setBrand({ legalFooter: e.target.value })}
              />
            </div>
            <div className="row mt4" style={{ gap: "var(--s5)", alignItems: "flex-end" }}>
              <div className="field">
                <label htmlFor="brAccent">Accent color</label>
                <div className="row">
                  <input
                    id="brAccent"
                    type="color"
                    className="color-input"
                    value={brand.colors.accent}
                    onChange={(e) => setBrand({ colors: { ...brand.colors, accent: e.target.value } })}
                  />
                  <span className="swatch" style={{ background: brand.colors.accent }} />
                  <span className="mono tiny">{brand.colors.accent}</span>
                </div>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="brLocale">Locale</label>
                <select
                  id="brLocale"
                  className="input"
                  value={brand.locale}
                  onChange={(e) => setBrand({ locale: e.target.value })}
                >
                  <option value="en-AE">en-AE (English, UAE)</option>
                  <option value="ar-AE">ar-AE (العربية — RTL)</option>
                </select>
              </div>
            </div>
            <p className="hint mt4">
              This maps 1:1 to <code>src/branding/brand.config.ts</code> — one file re-skins the whole
              platform for a tenant. Contrast for primary buttons is derived from the accent’s
              luminance, so any accent stays legible.
            </p>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Currencies &amp; tax</h2>
            {saved ? (
              <>
                <span className="top-spacer" />
                <span className="badge good">
                  <span className="dot" /> Saved
                </span>
              </>
            ) : null}
          </div>
          <div className="card-body">
            <div className="field">
              <label htmlFor="baseCur">Base currency</label>
              <select id="baseCur" className="input" value={draft.baseCurrency} disabled>
                <option value="AED">AED — UAE Dirham</option>
              </select>
              <span className="hint">
                Base is fixed per group. Foreign balances revalue to base with FX gain/loss.
              </span>
            </div>

            <div className="table-wrap mt4">
              <table className="data">
                <thead>
                  <tr>
                    <th>Currency</th>
                    <th className="num">Rate → AED</th>
                    <th>As of</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.fxRates.map((r: FxRate) => (
                    <tr key={r.currency}>
                      <td>{r.currency}</td>
                      <td className="num">
                        <input
                          className="input num"
                          inputMode="decimal"
                          aria-label={`${r.currency} rate to AED`}
                          value={r.rate}
                          onChange={(e) => patchRate(r.currency, Number(e.target.value) || 0)}
                          style={{ maxWidth: 110, marginInlineStart: "auto" }}
                        />
                      </td>
                      <td>{r.asOf}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row mt5" style={{ gap: "var(--s5)" }}>
              <div className="field">
                <label htmlFor="vatRate">VAT standard rate</label>
                <input
                  id="vatRate"
                  className="input"
                  inputMode="decimal"
                  value={(draft.vatRate * 100).toFixed(0)}
                  onChange={(e) => patch({ vatRate: (Number(e.target.value) || 0) / 100 })}
                  style={{ maxWidth: 90 }}
                />
              </div>
              <div className="field">
                <label htmlFor="ctRate">Corporate tax rate</label>
                <input
                  id="ctRate"
                  className="input"
                  inputMode="decimal"
                  value={(draft.ctRate * 100).toFixed(0)}
                  onChange={(e) => patch({ ctRate: (Number(e.target.value) || 0) / 100 })}
                  style={{ maxWidth: 90 }}
                />
              </div>
            </div>

            <div className="field mt4">
              <label htmlFor="sbr">Small Business Relief</label>
              <select
                id="sbr"
                className="input"
                value={sbrEnabled ? "on" : "off"}
                onChange={(e) => patch({ sbReliefRevenueCeiling: e.target.value === "on" ? 3_000_000_00 : null })}
              >
                <option value="off">Disabled</option>
                <option value="on">Enabled — revenue ≤ AED 3m</option>
              </select>
            </div>

            <div className="mt5 row" style={{ gap: "var(--s3)" }}>
              <button
                type="button"
                className="btn primary"
                onClick={save}
                disabled={!canEditSettings}
              >
                Save settings
              </button>
              {!canEditSettings ? (
                <span className="role-note" role="note">
                  Read-only — editing settings needs an Admin or Owner role.
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <MembersAccess />
    </section>
  );
}
